/**
 * DocumentationView — canvas view for a `documentation` workspace.
 *
 * A documentation workspace authors ONE template-based package document as its
 * single visible deliverable (it is an assembler, not a researcher). This view
 * renders that document with the per-document HUMAN approval action.
 *
 * Fix 6: the document is rendered + edited through the SAME report machinery
 * the research workspace uses — `reportMarkdown`'s shared renderer (markdown
 * formatting + citation chips) and the shared `ReportEditor` (inline editing) —
 * so a documentation doc reads and edits exactly like a research report. Edits
 * post the full markdown to the doc-file content endpoint, which emits a
 * DOC_FILE_SET (last-write-wins, like the report editor) — carrying dependency
 * links forward and clearing any prior approval, keeping the Step-1
 * approval/staleness semantics intact.
 *
 * Linked data: like research (RequirementsPhase) and production (ProcessViewer),
 * a documentation workspace mounts the SAME `UnifiedFindingsPanel` as a
 * right-hand slide-out — fed by `dictWorkspace.linked_outputs` (the read-time
 * plan-graph join already served for every workspace type). It surfaces the
 * UPSTREAM workspaces this document is assembled from ("Other workspace"
 * source), so the author can browse + cite the data the document is based upon,
 * and so the document's citation chips drill into their source. Mounted in
 * plain (non-spec) mode — spec-tracking is a research/production concern.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import { useProjectStore } from '../../stores/projectStore';
import { formatDateTime } from '../../utils/formatDateTime';
import { approveDocFile, updateDocFileContent } from '../../apiService';
import {
  buildBaseMarkdownComponents,
  preprocessDocumentMarkdown,
  type LinkedRequirementInfo,
} from '../workspace/reportMarkdown';
import ReportEditor from '../workspace/ReportEditor';
import ConfirmDialog from '../ui/ConfirmDialog';
import UnifiedFindingsPanel, { FindingsToggleButton } from '../workspace/UnifiedFindingsPanel';
import { formatProvenance } from './docProvenance';
import { FileText, CheckCircle2, Clock, Loader2, AlertCircle, Pencil, X, Check, ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react';
import type { DictDocFile } from '../../types/workspaceState';

interface DocumentationViewProps {
  projectId: string | null;
  workspaceId: string | null;
  authToken?: string;
  userId?: string;
}

/** Format an ISO timestamp safely — a malformed value renders nothing. */
function formatApprovedAt(value: string | null | undefined): string {
  if (!value) return '';
  const formatted = formatDateTime(value, { invalidFallback: '' });
  return formatted ? ` · ${formatted}` : '';
}

/**
 * Grounding badge — the documentation deliverable's validation status, the same
 * concept research/production deliverables carry. Reads the doc's grounding
 * verdict (every regulatory claim must reference the linked data) and renders a
 * compact, scannable chip; the title carries the detector summary.
 */
function DocGroundingBadge({ doc }: { doc: DictDocFile }) {
  const g = doc.grounding;
  const cls = 'inline-flex items-center gap-1 text-xs font-medium';
  if (!g) {
    return (
      <span className={`${cls} text-fg-tertiary`} title="Not yet validated — no grounding check on this document.">
        <ShieldAlert className="w-3.5 h-3.5" aria-hidden="true" /> Not validated
      </span>
    );
  }
  if (doc.grounding_stale) {
    return (
      <span className={`${cls} text-warning`} title="The document changed since it was last grounded — re-validate (re-run write/edit, or re-accept).">
        <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Re-validate
      </span>
    );
  }
  if (g.blocking) {
    // Count every blocking component across BOTH grounding modes: uncited
    // claims, plus dangling/ungrounded requirement refs (traceability) and
    // dangling markers (research report grounding).
    const n =
      (g.uncited_claim_count ?? 0) +
      (g.dangling_reqs?.length ?? 0) +
      (g.ungrounded_reqs?.length ?? 0) +
      (g.dangling_markers?.length ?? 0);
    return (
      <span className={`${cls} text-danger`} title={g.summary || 'Some claims do not trace to a grounded upstream requirement.'}>
        <ShieldAlert className="w-3.5 h-3.5" aria-hidden="true" /> {n} unreferenced
      </span>
    );
  }
  if (g.verdict === 'approved') {
    const cited = g.cited_claim_count ?? 0;
    const total = g.claim_count ?? 0;
    return (
      <span className={`${cls} text-success`} title={g.summary || `All ${total} claim(s) reference the linked data.`}>
        <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> Validated{total ? ` · ${cited}/${total} cited` : ''}
      </span>
    );
  }
  // flagged (advisory correspondence findings) — grounded, but worth a look.
  return (
    <span className={`${cls} text-warning`} title={g.summary || 'Grounded, with advisory citation-correspondence notes.'}>
      <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> Validated · advisories
    </span>
  );
}

export default function DocumentationView({
  workspaceId,
  authToken,
  userId,
}: DocumentationViewProps) {
  const dictWorkspace = useProjectStore((s) => s.dictWorkspace);
  const loadSpecificWorkspace = useProjectStore((s) => s.loadSpecificWorkspace);

  const [busyFileId, setBusyFileId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Linked-data panel state — mirrors the report-tab mount (App.tsx) and
  // ProcessViewer: a right-hand slide-out that compresses the document column.
  // `panelListWidth` is the collapsed-list width; `splitPercent` governs the
  // split once a reference detail is open (wider, like the report view).
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [panelListWidth, setPanelListWidth] = useState(320);
  const [splitPercent, setSplitPercent] = useState(45);
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);
  const [selectedOnlineSupportId, setSelectedOnlineSupportId] = useState<string | null>(null);
  const [selectedRequirementId, setSelectedRequirementId] = useState<string | null>(null);
  // Guards the detail open→closed transition so re-clicking the same chip
  // re-fires the panel's focus effect (value-gated props otherwise dead-click).
  const prevDetailOpenRef = useRef<boolean>(false);

  // A citation chip in the document opens the panel focused on its source —
  // the document's references drill into the upstream data they cite.
  const handleCitationClick = useCallback((findingId: string) => {
    setSelectedFindingId(findingId);
    setSelectedOnlineSupportId(null); // cross-type mutual exclusion
    setSelectedRequirementId(null);
    setIsPanelOpen(true);
  }, []);
  const handleOnlineRefClick = useCallback((onlineSupportId: string) => {
    setSelectedOnlineSupportId(onlineSupportId);
    setSelectedFindingId(null);
    setSelectedRequirementId(null);
    setIsPanelOpen(true);
  }, []);
  // A {req:REQ-XXX} chip drills into the upstream requirement it references —
  // the same "references drill into the data they cite" behaviour as citations,
  // here pointing at the linked upstream requirement it inherits its citation from.
  const handleRequirementRefClick = useCallback((_requirementId: string) => {
    setSelectedRequirementId(_requirementId);
    setSelectedFindingId(null);
    setSelectedOnlineSupportId(null);
    setIsPanelOpen(true);
  }, []);

  // Inline-edit state (one doc edits at a time — a documentation workspace
  // owns one document). ReportEditor is uncontrolled via the ref; we read its
  // markdown on save, mirroring ReportView.
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [savingFileId, setSavingFileId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const editorRef = useRef<{ getMarkdown: () => string } | null>(null);
  const editBaselineRef = useRef<string>('');
  const hasUnsavedRef = useRef<boolean>(false);

  // A documentation workspace owns ONE document; render every doc_file present
  // (normally exactly one) so nothing the agent authored is hidden.
  const docFiles = Object.values(dictWorkspace?.doc_files ?? {});

  // The shared base renderer — identical to the research report surface
  // (citation chips, tables, headings, lists, …). Citations resolve against
  // the workspace's markers/findings/online-support; clicking a chip focuses
  // the linked-data panel on its source (and highlights the active chip).
  // WS2: resolve every upstream spec_requirement into the map that backs the
  // document's {req:REQ-XXX} chips — requirement id → its inherited first-order
  // citation + grounding. Built from the linked-data join (report_library now
  // carries finding_refs + grounding_status on requirement refs).
  const linkedRequirements = useMemo(() => {
    const map: Record<string, LinkedRequirementInfo> = {};
    for (const lo of dictWorkspace?.linked_outputs ?? []) {
      for (const ref of lo.references ?? []) {
        if (ref.kind !== 'spec_requirement') continue;
        const reqId = (
          ref.ref_id.startsWith('req:') ? ref.ref_id.slice(4) : ref.ref_id
        ).toUpperCase();
        const citation =
          ref.finding_refs?.find((fr) => fr.citation_excerpt)?.citation_excerpt ??
          undefined;
        map[reqId] = {
          citation: citation ?? undefined,
          // Valid {req:} citation target (matches the backend traceability
          // gate): a design requirement always; anything else iff it carries
          // grounding (grounded status or a citation).
          grounded:
            ref.claim_kind === 'design' ||
            ref.grounding_status === 'grounded' ||
            (ref.finding_refs?.length ?? 0) > 0,
          statement: ref.detail || ref.summary,
          sourceWorkspaceName: lo.workspace_name,
        };
      }
    }
    return map;
  }, [dictWorkspace?.linked_outputs]);

  const baseComponents = useMemo(
    () =>
      buildBaseMarkdownComponents({
        markers: dictWorkspace?.markers ?? {},
        findings: dictWorkspace?.findings ?? {},
        onlineSupport: dictWorkspace?.online_support ?? {},
        onCitationClick: handleCitationClick,
        onOnlineRefClick: handleOnlineRefClick,
        selectedFindingId,
        selectedOnlineSupportId,
        linkedRequirements,
        onRequirementRefClick: handleRequirementRefClick,
        selectedRequirementId,
      }),
    [
      dictWorkspace?.markers,
      dictWorkspace?.findings,
      dictWorkspace?.online_support,
      handleCitationClick,
      handleOnlineRefClick,
      selectedFindingId,
      selectedOnlineSupportId,
      linkedRequirements,
      handleRequirementRefClick,
      selectedRequirementId,
    ],
  );

  // Toggle-badge count — surfaces upstream linked outputs first (the point of
  // the panel here), plus any local findings / online / uploaded refs.
  const linkedOutputs = dictWorkspace?.linked_outputs ?? [];
  const totalRefCount =
    linkedOutputs.length +
    Object.keys(dictWorkspace?.findings ?? {}).length +
    Object.keys(dictWorkspace?.online_support ?? {}).length +
    Object.keys(dictWorkspace?.uploaded_files ?? {}).length;

  const refresh = async () => {
    if (workspaceId && userId) {
      await loadSpecificWorkspace(workspaceId, userId);
    }
  };

  const handleApprove = async (fileId: string) => {
    if (!workspaceId) return;
    setError(null);
    setBusyFileId(fileId);
    try {
      const res = await approveDocFile(workspaceId, fileId, undefined, authToken);
      if (res.success) {
        await refresh();
      } else {
        setError(res.error || 'Failed to approve');
      }
    } finally {
      setBusyFileId(null);
    }
  };

  const handleStartEdit = (fileId: string, content: string) => {
    setSaveError(null);
    editBaselineRef.current = content;
    hasUnsavedRef.current = false;
    setEditingFileId(fileId);
  };

  const handleEditorChange = (markdown: string) => {
    hasUnsavedRef.current = markdown !== editBaselineRef.current;
    if (saveError) setSaveError(null);
  };

  const discardEdits = () => {
    setEditingFileId(null);
    setSaveError(null);
    hasUnsavedRef.current = false;
    editBaselineRef.current = '';
    setDiscardDialogOpen(false);
  };

  const handleCancelEdit = () => {
    if (hasUnsavedRef.current) {
      setDiscardDialogOpen(true);
      return;
    }
    discardEdits();
  };

  const handleSave = async (fileId: string) => {
    if (!workspaceId || savingFileId) return;
    let markdown: string;
    try {
      markdown = editorRef.current?.getMarkdown() ?? editBaselineRef.current;
    } catch {
      setSaveError('Could not read the editor content. Reload the page and try again.');
      return;
    }
    setSavingFileId(fileId);
    setSaveError(null);
    const res = await updateDocFileContent(workspaceId, fileId, markdown, authToken);
    setSavingFileId(null);
    if (!res.success) {
      setSaveError(res.error || 'Failed to save document');
      return;
    }
    setEditingFileId(null);
    hasUnsavedRef.current = false;
    editBaselineRef.current = '';
    await refresh();
  };

  return (
    <div ref={containerRef} className="h-full flex">
      {/* Document column — relative wrapper so the toggle button stays fixed
          while the document scrolls (mirrors the report-tab layout). */}
      <div className="flex-1 h-full relative min-w-0">
        <div className="h-full overflow-y-auto p-6 bg-bg-warm">
          <div className="flex items-center gap-2 mb-6">
            <FileText className="w-5 h-5 text-secondary" />
            <h2 className="text-lg font-semibold text-fg">Documentation</h2>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 p-3 bg-surface rounded border border-danger/40 text-sm text-danger">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {docFiles.length === 0 ? (
            <div className="text-sm text-fg-tertiary">
              No document authored yet. This workspace authors one template-based
              document from the upstream research that feeds it.
            </div>
          ) : (
            <div className="space-y-4">
              {docFiles.map((f) => {
                const provenance = formatProvenance(f);
                const isEditing = editingFileId === f.file_id;
                const isSaving = savingFileId === f.file_id;
                return (
                  <div key={f.file_id} className="bg-surface rounded-lg border border-border-default">
                    <div className="flex items-center justify-between gap-2 p-3 border-b border-border-default">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-fg-secondary truncate">
                          {f.title || f.file_id}
                        </div>
                        <div className="text-xs text-fg-tertiary">
                          {f.file_id}
                          {provenance && ` · ${provenance}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!isEditing && <DocGroundingBadge doc={f} />}
                        {isEditing ? (
                          <>
                            {saveError && (
                              <span className="text-xs text-danger mr-1" role="alert">
                                {saveError}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              disabled={isSaving}
                              aria-label="Cancel edits"
                              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold bg-surface text-fg border border-border-default hover:bg-bg-warm hover:border-border-strong transition-colors disabled:opacity-60"
                            >
                              <X className="w-3.5 h-3.5" aria-hidden="true" />
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSave(f.file_id)}
                              disabled={isSaving}
                              aria-label="Save document"
                              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold transition-colors ${
                                isSaving
                                  ? 'bg-surface text-fg-tertiary border border-border-default cursor-wait'
                                  : 'bg-primary text-on-primary border border-primary hover:bg-primary-hover'
                              }`}
                            >
                              {isSaving ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                              ) : (
                                <Check className="w-3.5 h-3.5" aria-hidden="true" />
                              )}
                              {isSaving ? 'Saving…' : 'Save'}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => handleStartEdit(f.file_id, f.content || '')}
                              disabled={!workspaceId}
                              aria-label="Edit document"
                              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold bg-surface text-fg border border-border-default hover:bg-bg-warm hover:border-border-strong transition-colors disabled:opacity-50"
                            >
                              <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                              Edit
                            </button>
                            {f.approved_by ? (
                              <span className="flex items-center gap-1 text-xs font-medium text-success">
                                <CheckCircle2 className="w-4 h-4" />
                                Approved
                              </span>
                            ) : (
                              <button
                                onClick={() => handleApprove(f.file_id)}
                                disabled={busyFileId === f.file_id || !workspaceId}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-surface text-fg-secondary text-sm font-medium rounded-md border border-border-default hover:bg-primary-tint disabled:opacity-50 transition-colors"
                              >
                                {busyFileId === f.file_id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Clock className="w-3.5 h-3.5" />
                                )}
                                Approve
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {f.approved_by && !isEditing && (
                      <div className="text-xs text-fg-tertiary px-3 pt-2">
                        by {f.approved_by}
                        {f.approver_role ? ` (${f.approver_role})` : ''}
                        {formatApprovedAt(f.approved_at)}
                      </div>
                    )}
                    <div className="p-4">
                      {isEditing ? (
                        <ReportEditor
                          ref={editorRef}
                          initialContent={f.content || ''}
                          onChange={handleEditorChange}
                        />
                      ) : (
                        <div className="report-content max-w-none">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm, remarkMath]}
                            rehypePlugins={[rehypeKatex]}
                            components={baseComponents}
                          >
                            {preprocessDocumentMarkdown(f.content || '')}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {/* Toggle button overlays the document — outside the scroll container so
            it stays fixed. Shown only when there's linked data to open. */}
        {!isPanelOpen && totalRefCount > 0 && (
          <FindingsToggleButton
            findingsCount={totalRefCount}
            onToggle={() => setIsPanelOpen(true)}
          />
        )}
      </div>

      {/* Linked-data panel — flex sibling, compresses the document column.
          Stays mounted while closed (width 0) for a smooth slide; non-spec
          mode (no specProjectId) renders upstream outputs as "Other workspace". */}
      <div
        className="h-full flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden"
        style={{
          width: isPanelOpen
            ? isDetailOpen
              ? `${100 - splitPercent}%`
              : panelListWidth
            : 0,
        }}
      >
        <UnifiedFindingsPanel
          allFindings={dictWorkspace?.findings || {}}
          allMarkers={dictWorkspace?.markers || {}}
          allOnlineSupport={dictWorkspace?.online_support || {}}
          allUploadedFiles={dictWorkspace?.uploaded_files || {}}
          linkedOutputs={linkedOutputs}
          selectedFindingId={selectedFindingId}
          selectedOnlineSupportId={selectedOnlineSupportId}
          selectedRequirementId={selectedRequirementId}
          isOpen={isPanelOpen}
          workspaceId={workspaceId || undefined}
          compartmentId={dictWorkspace?.compartment_id || 'default'}
          onToggle={() => setIsPanelOpen((v) => !v)}
          onFindingSelect={(id) => {
            setSelectedFindingId(id);
            setSelectedOnlineSupportId(null);
            setIsPanelOpen(true);
          }}
          onClose={() => {
            setSelectedFindingId(null);
            setSelectedOnlineSupportId(null);
            setSelectedRequirementId(null);
            setIsDetailOpen(false);
            setIsPanelOpen(false);
          }}
          onDetailOpenChange={(open) => {
            setIsDetailOpen(open);
            // Clear the chip-driven selection only on a real open→closed
            // transition, so re-clicking the same chip reopens the detail.
            if (prevDetailOpenRef.current && !open) {
              setSelectedFindingId(null);
              setSelectedOnlineSupportId(null);
              setSelectedRequirementId(null);
            }
            prevDetailOpenRef.current = open;
          }}
          onPanelResize={(delta: number) => {
            if (isDetailOpen) {
              const containerWidth =
                containerRef.current?.offsetWidth || window.innerWidth;
              const pctDelta = (delta / containerWidth) * 100;
              setSplitPercent((p) => Math.max(30, Math.min(75, p - pctDelta)));
            } else {
              setPanelListWidth((w) => Math.max(220, Math.min(520, w - delta)));
            }
          }}
        />
      </div>
      <ConfirmDialog
        open={discardDialogOpen}
        onCancel={() => setDiscardDialogOpen(false)}
        onConfirm={discardEdits}
        title="Discard unsaved edits?"
        description="Your changes to this document will be lost."
        confirmLabel="Discard"
        variant="destructive"
      />
    </div>
  );
}
